<template>
    <div class="creativex-report">
        <div class="preflight-info">
            <h4><span>CREATIVEX ID:</span> {{ report.requestId }}</h4>

            <div class="preflight-info-table">
                <div class="preflight-info-table-row">
                    <span>Analysis requested at:</span>
                    <span>{{ new Date(created).toLocaleString() }}</span>
                </div>
                <div v-if="report.completedAt" class="preflight-info-table-row">
                    <span>Analysis completed at:</span>
                    <span>{{ new Date(report.completedAt).toLocaleString() }}</span>
                </div>
                <br />
                <div v-if="report.name" class="preflight-info-table-row">
                    <span>Variant name:</span>
                    <Ellipsis :text="report.name" :length="68" max-width="500" tooltip />
                </div>
                <div v-if="report.creative?.scorecardUrl" class="preflight-info-table-row">
                    <span>Scorecard url:</span>
                    <span>
                        <a :href="report.creative.scorecardUrl" target="_blank">
                            {{ formatLinkLabel(report.creative.scorecardUrl) }}
                            <Icon type="ios-open-outline" size="13" />
                        </a>
                    </span>
                </div>
                <br />
                <div v-if="report.channel" class="preflight-info-table-row">
                    <span>Channel:</span>
                    <span>{{ report.channel }}</span>
                </div>
                <div v-if="report.publisher" class="preflight-info-table-row">
                    <span>Publisher:</span>
                    <span>{{ report.publisher }}</span>
                </div>
                <div v-if="report.placement" class="preflight-info-table-row">
                    <span>Placement:</span>
                    <span>{{ report.placement }}</span>
                </div>
            </div>
        </div>

        <div v-if="score" class="preflight-guidelines">
            <table>
                <thead>
                    <tr>
                        <th>Guidelines</th>
                        <th>Weight</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(guideline, index) in score.guidelines" :key="index">
                        <td>{{ guideline.name }}</td>
                        <td>{{ guideline.weight }}</td>
                        <td>
                            <Icon v-if="guideline.passed" type="md-checkbox-outline" />
                            <Icon v-else type="md-warning" />
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <th>Score</th>
                        <th>{{ score.value * 100 }}%</th>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { CreativeXPreflightHistory } from "@/utils/Preflight/getPreflightHistory";

const props = defineProps<{
    report: Extract<CreativeXPreflightHistory, { status: "COMPLETED" }>["report"];
    created: string;
}>();

const score = computed(() => props.report.creative?.score);

const formatLinkLabel = (url: string): string => {
    const maxLength = 50;
    return url.length > maxLength ? `${url.substring(0, maxLength)}...` : url;
};
</script>

<style scoped>
.creativex-report {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.preflight-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.preflight-info h4 {
    margin: 0 0 12px;
}

.preflight-info-table {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.preflight-info-table-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
}

.preflight-info-table-row > span:first-child {
    font-weight: 600;
}

.preflight-guidelines table {
    margin: 0;
    padding: 0;
    width: 100%;
    border-collapse: collapse;
}

.preflight-guidelines th,
.preflight-guidelines td {
    padding: 8px;
    text-align: left;
}
</style>
